import React, { useState, useRef } from 'react';
import { 
 CloudArrowUpIcon, 
 PlayIcon, 
 StopIcon,
 CheckCircleIcon,
 ExclamationTriangleIcon,
 MicrophoneIcon
} from '@heroicons/react/24/outline';

const VoiceFileUploader = ({ userId, onUploadComplete }) => {
 const [uploadedFiles, setUploadedFiles] = useState([]);
 const [isUploading, setIsUploading] = useState(false);
 const [isProcessing, setIsProcessing] = useState(false);
 const [isTraining, setIsTraining] = useState(false);
 const [trainingStatus, setTrainingStatus] = useState(null);
 const fileInputRef = useRef(null);
 const audioRef = useRef(null);
 const [playingFile, setPlayingFile] = useState(null);

 const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

 const supportedFormats = [
 'audio/wav', 'audio/mp3', 'audio/mpeg', 'audio/ogg', 'audio/webm',
 'audio/m4a', 'audio/aac', 'audio/flac'
 ];

 const handleFileSelect = (event) => {
 const files = Array.from(event.target.files);
 const validFiles = files.filter(file => 
 supportedFormats.includes(file.type) || 
 file.name.toLowerCase().endsWith('.wav') ||
 file.name.toLowerCase().endsWith('.mp3') ||
 file.name.toLowerCase().endsWith('.m4a') ||
 file.name.toLowerCase().endsWith('.dat') // WhatsApp audio files
 );

 if (validFiles.length === 0) {
 alert('Please select valid audio files (WAV, MP3, M4A, etc.)');
 return;
 }

 setUploadedFiles(prev => [...prev, ...validFiles.map(file => ({
 file,
 name: file.name,
 size: file.size,
 type: file.type,
 uploaded: false,
 processing: false
 }))]);
 };

 const uploadFile = async (fileData, index) => {
 try {
 const formData = new FormData();
 formData.append('audio', fileData.file);
 formData.append('sampleIndex', index);

 const response = await fetch(`${API_BASE}/api/voice-training/upload/${userId}`, {
 method: 'POST',
 body: formData,
 });

 const result = await response.json();
 return result.success;
 } catch (error) {
 console.error('Upload error:', error);
 return false;
 }
 };

 const uploadAllFiles = async () => {
 if (uploadedFiles.length === 0) return;

 setIsUploading(true);
 const updatedFiles = [...uploadedFiles];

 for (let i = 0; i < updatedFiles.length; i++) {
 updatedFiles[i].processing = true;
 setUploadedFiles([...updatedFiles]);

 const success = await uploadFile(updatedFiles[i], i + 1);
 updatedFiles[i].uploaded = success;
 updatedFiles[i].processing = false;
 setUploadedFiles([...updatedFiles]);

 if (!success) {
 console.error(`Failed to upload ${updatedFiles[i].name}`);
 }
 }

 setIsUploading(false);
 
 // Check if all files uploaded successfully
 const allUploaded = updatedFiles.every(file => file.uploaded);
 if (allUploaded && onUploadComplete) {
 onUploadComplete();
 }
 };

 const processVoiceSamples = async () => {
 setIsProcessing(true);
 try {
 const response = await fetch(`${API_BASE}/api/voice-training/process`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({ userId }),
 });
 const data = await response.json();
 if (data.success) {
 setTrainingStatus(prev => ({ ...prev, processed: true }));
 }
 } catch (error) {
 console.error('Error processing voice samples:', error);
 } finally {
 setIsProcessing(false);
 }
 };

 const trainVoiceModel = async () => {
 setIsTraining(true);
 try {
 const response = await fetch(`${API_BASE}/api/voice-training/train`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({ userId }),
 });
 const data = await response.json();
 if (data.success) {
 setTrainingStatus(prev => ({ ...prev, trained: true }));
 }
 } catch (error) {
 console.error('Error training voice model:', error);
 } finally {
 setIsTraining(false);
 }
 };

 const playAudio = (file) => {
 if (playingFile === file.name) {
 if (audioRef.current) {
 audioRef.current.pause();
 setPlayingFile(null);
 }
 } else {
 const url = URL.createObjectURL(file.file);
 if (audioRef.current) {
 audioRef.current.src = url;
 audioRef.current.play();
 setPlayingFile(file.name);
 }
 }
 };

 const formatFileSize = (bytes) => {
 if (bytes === 0) return '0 Bytes';
 const k = 1024;
 const sizes = ['Bytes', 'KB', 'MB', 'GB'];
 const i = Math.floor(Math.log(bytes) / Math.log(k));
 return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
 };

 const getFileIcon = (file) => {
 if (file.processing) {
 return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>;
 } else if (file.uploaded) {
 return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
 } else {
 return <ExclamationTriangleIcon className="h-4 w-4 text-muted" />;
 }
 };

 return (
 <div className="bg-bgSection p-6 rounded-lg space-y-6">
 <div className="flex items-center justify-between">
 <h2 className="text-xl font-bold text-white">Upload Your Voice Recordings</h2>
 <div className="text-sm text-muted">
 {uploadedFiles.length} files selected
 </div>
 </div>

 {/* File Upload Area */}
 <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center">
 <CloudArrowUpIcon className="h-12 w-12 text-muted mx-auto mb-4" />
 <h3 className="text-lg font-medium text-white mb-2">Select Voice Files</h3>
 <p className="text-muted mb-4">
 Upload your voice recordings (WAV, MP3, M4A, or WhatsApp audio files)
 </p>
 <p className="text-sm text-muted mb-4">
 Recommended: 5-10 files, 10-30 seconds each, clear audio quality
 </p>
 
 <input
 ref={fileInputRef}
 type="file"
 multiple
 accept="audio/*"
 onChange={handleFileSelect}
 className="hidden"
 />
 
 <button
 onClick={() => fileInputRef.current?.click()}
 className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors"
 >
 Choose Files
 </button>
 </div>

 {/* File List */}
 {uploadedFiles.length > 0 && (
 <div className="space-y-3">
 <h3 className="text-lg font-medium text-white">Selected Files</h3>
 {uploadedFiles.map((file, index) => (
 <div key={index} className="bg-gray-700 p-4 rounded-lg flex items-center justify-between">
 <div className="flex items-center gap-3">
 {getFileIcon(file)}
 <div>
 <p className="text-white font-medium">{file.name}</p>
 <p className="text-sm text-muted">
 {formatFileSize(file.size)} • {file.type || 'Unknown format'}
 </p>
 </div>
 </div>
 
 <div className="flex items-center gap-2">
 <button
 onClick={() => playAudio(file)}
 className="p-2 text-muted hover:text-white transition-colors"
 title="Play audio"
 >
 {playingFile === file.name ? (
 <StopIcon className="h-4 w-4" />
 ) : (
 <PlayIcon className="h-4 w-4" />
 )}
 </button>
 
 {file.uploaded && (
 <span className="text-xs text-green-400">Uploaded</span>
 )}
 </div>
 </div>
 ))}
 </div>
 )}

 {/* Upload Button */}
 {uploadedFiles.length > 0 && (
 <div className="flex gap-3">
 <button
 onClick={uploadAllFiles}
 disabled={isUploading || uploadedFiles.every(f => f.uploaded)}
 className="bg-green-500 hover:bg-green-600 disabled:bg-gray-500 text-white px-6 py-2 rounded-lg transition-colors"
 >
 {isUploading ? 'Uploading...' : 'Upload All Files'}
 </button>
 
 {uploadedFiles.every(f => f.uploaded) && (
 <button
 onClick={processVoiceSamples}
 disabled={isProcessing || trainingStatus?.processed}
 className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-500 text-white px-6 py-2 rounded-lg transition-colors"
 >
 {isProcessing ? 'Processing...' : 'Process Samples'}
 </button>
 )}
 
 {trainingStatus?.processed && (
 <button
 onClick={trainVoiceModel}
 disabled={isTraining || trainingStatus?.trained}
 className="bg-purple-500 hover:bg-purple-600 disabled:bg-gray-500 text-white px-6 py-2 rounded-lg transition-colors"
 >
 {isTraining ? 'Training...' : 'Train Voice Model'}
 </button>
 )}
 </div>
 )}

 {/* Training Status */}
 {trainingStatus?.trained && (
 <div className="bg-green-900 border border-green-700 p-4 rounded-lg">
 <div className="flex items-center gap-2 mb-2">
 <CheckCircleIcon className="h-5 w-5 text-green-400" />
 <h4 className="font-medium text-green-400">Voice Training Complete!</h4>
 </div>
 <p className="text-sm text-green-300">
 Your custom voice model is ready. You can now generate videos with your own voice.
 </p>
 </div>
 )}

 {/* Audio Player */}
 <audio
 ref={audioRef}
 onEnded={() => setPlayingFile(null)}
 className="hidden"
 />

 {/* Instructions */}
 <div className="bg-gray-700 p-4 rounded-lg">
 <h3 className="font-medium text-white mb-2">Recording Tips</h3>
 <ul className="text-sm text-muted space-y-1">
 <li>• Speak clearly and at a normal pace</li>
 <li>• Use a quiet environment with minimal background noise</li>
 <li>• Record 10-30 seconds per sample</li>
 <li>• Include different types of content (questions, explanations, etc.)</li>
 <li>• WhatsApp audio files (.dat) are supported and will be converted</li>
 </ul>
 </div>
 </div>
 );
};

export default VoiceFileUploader;









